import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Wind, Sun, Compass, Globe2 } from "lucide-react";
import EditorialMarquee from "@/components/home/EditorialMarquee";
import { useLang } from "@/i18n/LanguageContext";

const SITES = {
  fr: [
    { name: "Toulouse", role: "L'idée originelle", body: "La maîtrise humaine, le génie de l'eau, la brique rose qui s'éveille et lance le grand défi." },
    { name: "Castelnau d'Estrétefonds", role: "Le point d'ancrage", body: "Le gardien des premiers pas — là où la terre et le canal s'épousent dans la mémoire des plaines." },
    { name: "Saint-Ferréol", role: "La source nourricière", body: "Le réservoir des rêves. Là où le génie dompte l'élément." },
    { name: "Castelnaudary", role: "Le grand bassin", body: "Le cœur battant du voyage. L'eau prend son élan et s'offre sa première escale de splendeur." },
    { name: "Le Malpas · Colombiers", role: "Le franchissement de l'impossible", body: "Le courage pur. La roche percée par la seule volonté humaine." },
    { name: "Loupian · Étang de Thau", role: "La transmission au monde", body: "L'expiration finale. L'ouverture. Le canal devient horizon." },
  ],
  en: [
    { name: "Toulouse", role: "The original idea", body: "Human mastery, the genius of water, the pink brick awakening and launching the great challenge." },
    { name: "Castelnau d'Estrétefonds", role: "The anchor point", body: "The guardian of the first steps — where the land and the canal embrace in the memory of the plains." },
    { name: "Saint-Ferréol", role: "The nurturing source", body: "The reservoir of dreams. Where genius tames the element." },
    { name: "Castelnaudary", role: "The great basin", body: "The beating heart of the journey. The water gathers momentum and grants itself its first stop of splendour." },
    { name: "Le Malpas · Colombiers", role: "Crossing the impossible", body: "Pure courage. The rock pierced by human will alone." },
    { name: "Loupian · Étang de Thau", role: "The transmission to the world", body: "The final exhalation. The opening. The canal becomes horizon." },
  ],
};

const ACTES = {
  fr: [
    { n: "01", Icon: Wind, title: "Le \u00ab Dernier Fil \u00bb", body: "Une ligne artistique d'une discrétion absolue : des tissus, de la lumière, des câbles fins, des rubans, des installations suspendues par le vent. Ici, le vent devient co-auteur. Et à Loupian, le vent travaille gratuitement. C'est l'assistant logistique le plus fidèle du littoral." },
    { n: "02", Icon: Compass, title: "Les œuvres tournées vers l'horizon", body: "Les installations ne font pas face au visiteur. Elles regardent le large. Comme si les artistes parlaient au monde, ou envoyaient un signal, un message de paix, au-delà de la Méditerranée." },
    { n: "03", Icon: Globe2, title: "Le \u00ab Belvédère UNESCO des 194 pays \u00bb", body: "Pas de bannières criardes, mais l'élégance du monde réuni au bord de l'eau : 194 lignes de lumière, 194 noms gravés dans le vent, 194 fragments de textes universels, 194 couleurs discrètes qui se fondent dans le bleu.", list: ["194 lignes de lumière", "194 noms gravés dans le vent", "194 fragments de textes universels", "194 couleurs discrètes qui se fondent dans le bleu"] },
    { n: "04", Icon: Sun, title: "Le grand final au coucher du soleil", body: "Loupian devient l'image finale de Canal'Art 2026. Pas la plus spectaculaire, mais la plus vibrante, la plus émotionnelle. Après avoir traversé tout le parcours — de la brique de Toulouse aux rives de Castelnau, du grand miroir de Castelnaudary aux pierres du Malpas — le voyageur comprend enfin : le canal n'était pas seulement un ouvrage hydraulique. C'était une ligne humaine, tracée de main d'homme, traversant le territoire pour s'offrir à la mer." },
  ],
  en: [
    { n: "01", Icon: Wind, title: "The \u00ab Last Thread \u00bb", body: "An artistic line of absolute discretion: fabrics, light, thin cables, ribbons, installations suspended by the wind. Here, the wind becomes co-author. And in Loupian, the wind works for free. It is the coast's most faithful logistics assistant." },
    { n: "02", Icon: Compass, title: "Works facing the horizon", body: "The installations do not face the visitor. They look out to sea. As if the artists were speaking to the world, or sending a signal, a message of peace, beyond the Mediterranean." },
    { n: "03", Icon: Globe2, title: "The \u00ab UNESCO Belvedere of the 194 countries \u00bb", body: "No garish banners, but the elegance of the world gathered at the water's edge: 194 lines of light, 194 names engraved in the wind, 194 fragments of universal texts, 194 discreet colours melting into the blue.", list: ["194 lines of light", "194 names engraved in the wind", "194 fragments of universal texts", "194 discreet colours melting into the blue"] },
    { n: "04", Icon: Sun, title: "The grand finale at sunset", body: "Loupian becomes the final image of Canal'Art 2026. Not the most spectacular, but the most vibrant, the most emotional. After crossing the whole trail — from the brick of Toulouse to the banks of Castelnau, from the great mirror of Castelnaudary to the stones of Malpas — the traveller finally understands: the canal was not merely a hydraulic work. It was a human line, drawn by hand, crossing the land to offer itself to the sea." },
  ],
};

const LITANIE = {
  fr: [
    ["Toulouse", "L'idée."], ["Le Canal", "La volonté."], ["Castelnau & Castelnaudary", "La traversée. Le grand bassin des mémoires."], ["Saint-Ferréol", "La source et le génie."], ["Le Malpas", "Le courage."], ["Les arbres", "La mémoire vivante."], ["Loupian", "L'expiration finale. L'ouverture au monde."],
  ],
  en: [
    ["Toulouse", "The idea."], ["The Canal", "The will."], ["Castelnau & Castelnaudary", "The crossing. The great basin of memories."], ["Saint-Ferréol", "The source and the genius."], ["Le Malpas", "The courage."], ["The trees", "The living memory."], ["Loupian", "The final exhalation. The opening to the world."],
  ],
};

function StanzaLine({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.05 }}
      transition={{ duration: 0.9, delay, ease: [0.65, 0, 0.35, 1] }}
    >
      {children}
    </motion.div>
  );
}

export default function RecitPage() {
  const { lang } = useLang();
  const en = lang === "en";
  const sites = SITES[lang];
  const actes = ACTES[lang];
  const litanie = LITANIE[lang];
  return (
    <main className="pt-32 pb-20" data-testid="recit-page">
      {/* Hero */}
      <section className="px-6 md:px-12 max-w-[1400px] mx-auto">
        <div className="meta-label text-cinabre">§ {en ? "The Story · Poetic Geography" : "Le Récit · Géographie Poétique"}</div>
        <h1 className="font-serif font-light text-6xl md:text-8xl lg:text-9xl leading-[0.88] tracking-tight mt-8 max-w-[19ch]">
          {en ? <>This is not an <em className="italic">exhibition</em>.</> : <>Ce n'est pas une <em className="italic">exposition</em>.</>}
        </h1>
        <p className="mt-8 font-serif text-2xl md:text-4xl italic leading-[1.35] max-w-4xl text-fg/85">
          {en ? <>It is a poetic geography on the scale of a UNESCO territory — a <span className="not-italic text-cinabre">lifeline</span>, a continuous breath, a territory-artwork.</> : <>C'est une géographie poétique à l'échelle d'un territoire UNESCO — une <span className="not-italic text-cinabre">ligne de vie</span>, un souffle continu, une œuvre-territoire.</>}
        </p>
      </section>

      {/* Prelude — sites as manifesto */}
      <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <div className="meta-label text-fg/60">{en ? <>— From the original brick<br/>to the liquid infinite</> : <>— De la brique originelle<br/>à l'infini liquide</>}</div>
        </div>
        <div className="md:col-span-8 divide-y divide-fg/15">
          {sites.map((s, i) => (
            <StanzaLine key={s.name} delay={i * 0.05}>
              <div className="py-10 md:py-12">
                <div className="flex items-baseline justify-between flex-wrap gap-3">
                  <h3 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">{s.name}</h3>
                  <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre">{s.role}</div>
                </div>
                <p className="mt-4 text-fg/80 leading-relaxed text-lg max-w-2xl">{s.body}</p>
              </div>
            </StanzaLine>
          ))}
        </div>
      </section>

      <EditorialMarquee />

      {/* Dilution — Loupian narrative */}
      <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1200px] mx-auto" data-testid="recit-dilution">
        <div className="meta-label text-cinabre">§ {en ? "The canal dissolving into the world" : "La dilution du canal dans le monde"}</div>
        <h2 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight mt-6 max-w-3xl">
          {en ? <>Here the canal <em className="italic">becomes horizon</em>.</> : <>Ici le canal <em className="italic">devient horizon</em>.</>}
        </h2>

        <div className="mt-14 grid md:grid-cols-2 gap-12">
          <div className="font-serif text-2xl md:text-3xl leading-[1.35] text-fg/90 space-y-4">
            <StanzaLine>{en ? "After the tunnels," : "Après les tunnels,"}</StanzaLine>
            <StanzaLine delay={0.05}>{en ? "the locks," : "les écluses,"}</StanzaLine>
            <StanzaLine delay={0.1}>{en ? "the villages," : "les villages,"}</StanzaLine>
            <StanzaLine delay={0.15}>{en ? "the trees," : "les arbres,"}</StanzaLine>
            <StanzaLine delay={0.2}>{en ? "the narrow passages," : "les passages étroits,"}</StanzaLine>
            <StanzaLine delay={0.3}><span className="italic">{en ? "suddenly you arrive:" : "on arrive soudain :"}</span></StanzaLine>
            <StanzaLine delay={0.4}>{en ? "at the sky," : "au ciel,"}</StanzaLine>
            <StanzaLine delay={0.45}>{en ? "at the wind," : "au vent,"}</StanzaLine>
            <StanzaLine delay={0.5}>{en ? "at the lagoon," : "à l'étang,"}</StanzaLine>
            <StanzaLine delay={0.55}><span className="text-cinabre">{en ? "at the total opening." : "à l'ouverture totale."}</span></StanzaLine>
          </div>
          <div className="text-fg/80 text-lg leading-relaxed space-y-6">
            {en ? (
              <>
                <p>In Loupian, the Fil du Midi does not say <em>"here the canal ends"</em>. It whispers: <em>"here the canal becomes horizon"</em>.</p>
                <p>The waterline, in the distance, almost looks like a border… erased.</p>
                <p className="border-l-2 border-cinabre pl-6 font-serif italic text-2xl">"The water joins the world."</p>
                <p>Yesterday, the Canal du Midi linked the Atlantic to the Mediterranean, trade to territories, peoples to cultures. In Loupian, the Fil du Midi symbolically links <strong className="font-medium text-fg">continents</strong>, <strong className="font-medium text-fg">migrations</strong>, <strong className="font-medium text-fg">sea routes</strong>, <strong className="font-medium text-fg">world imaginaries</strong>.</p>
              </>
            ) : (
              <>
                <p>À Loupian, le Fil du Midi ne raconte pas <em>« ici le canal s'arrête »</em>. Il murmure : <em>« ici le canal devient horizon »</em>.</p>
                <p>La ligne d'eau, au loin, ressemble presque à une frontière… effacée.</p>
                <p className="border-l-2 border-cinabre pl-6 font-serif italic text-2xl">« L'eau rejoint le monde. »</p>
                <p>Le Canal du Midi reliait hier l'Atlantique à la Méditerranée, le commerce aux territoires, les peuples aux cultures. À Loupian, le Fil du Midi relie symboliquement les <strong className="font-medium text-fg">continents</strong>, les <strong className="font-medium text-fg">migrations</strong>, les <strong className="font-medium text-fg">routes maritimes</strong>, les <strong className="font-medium text-fg">imaginaires mondiaux</strong>.</p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Les Quatre Actes */}
      <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1400px] mx-auto" data-testid="recit-actes">
        <div className="meta-label text-cinabre">§ {en ? "The Four Acts of the Horizon" : "Les Quatre Actes de l'Horizon"}</div>
        <h2 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight mt-6 max-w-3xl">
          {en ? <>Four gestures, one <em className="italic">breath</em>.</> : <>Quatre gestes, un <em className="italic">souffle</em>.</>}
        </h2>

        <div className="mt-16 grid md:grid-cols-2 gap-4">
          {actes.map((a, i) => (
            <motion.article
              key={a.n}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: (i % 2) * 0.05 }}
              className="border border-fg/15 p-8 md:p-10 group hover:bg-fg hover:text-bg transition-colors duration-500"
              data-testid={`recit-acte-${a.n}`}
            >
              <div className="flex items-start justify-between gap-6">
                <span className="font-mono text-[11px] tracking-[0.24em] uppercase text-cinabre">{en ? "Act" : "Acte"} {a.n}</span>
                <a.Icon size={22} className="shrink-0 opacity-70" strokeWidth={1.2} />
              </div>
              <h3 className="font-serif text-3xl md:text-4xl leading-[1.05] tracking-tight mt-6">{a.title}</h3>
              <p className="mt-6 leading-relaxed opacity-90">{a.body}</p>
              {a.list && (
                <ul className="mt-6 space-y-2 font-mono text-[11px] tracking-[0.18em] uppercase opacity-70">
                  {a.list.map((l) => <li key={l}>· {l}</li>)}
                </ul>
              )}
            </motion.article>
          ))}
        </div>
      </section>

      {/* Envisager Loupian — visual dyptich */}
      <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1600px] mx-auto" data-testid="recit-envisager">
        <div className="grid md:grid-cols-12 gap-12 mb-14">
          <div className="md:col-span-7">
            <div className="meta-label text-cinabre">§ {en ? "Envisioning Loupian · Provisional vision" : "Envisager Loupian · Vision provisoire"}</div>
            <h2 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight mt-6">
              {en ? <>A belvedere,<br/>a <em className="italic">sentence</em>, a drawing.</> : <>Un belvédère,<br/>une <em className="italic">phrase</em>, un dessin.</>}
            </h2>
          </div>
          <div className="md:col-span-5 self-end">
            <p className="text-fg/80 leading-relaxed text-lg">
              {en ? "Two possible gestures. At the lagoon's edge, a sentence suspended in the wind — each country offers a fragment of text, a number, a QR code. Further on, in the lock-keeper's house, three faces from elsewhere stand in silence: the world, gathered at the water's edge." : "Deux gestes possibles. Au bord de l'étang, une phrase suspendue au vent — chaque pays offre un fragment de texte, un numéro, un QR code. Plus loin, dans la maison éclusière, trois visages venus d'ailleurs se tiennent en silence : le monde, réuni au bord de l'eau."}
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-6 md:gap-8">
          <motion.figure
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="md:col-span-7 group"
            data-testid="envisager-ribbon"
          >
            <div className="border border-fg/15 overflow-hidden bg-fg/[0.02] aspect-[16/10]">
              <img
                src="/belvedere-kossi-homawoo.jpg"
                alt="Belvédère UNESCO — ruban suspendu au vent portant un fragment de Kossi Homawoo (Togo), sur les rives de l'étang de Thau"
                className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105"
                loading="lazy"
              />
            </div>
            <figcaption className="mt-4">
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre">{en ? "Act 03 · UNESCO Belvedere of the 194 countries" : "Acte 03 · Belvédère UNESCO des 194 pays"}</div>
              <div className="mt-1 font-serif italic text-lg md:text-xl leading-tight">
                {en ? '"The horizon is not a limit, it is an invitation to seek within ourselves the space that connects us to others."' : "« L'horizon n'est pas une limite, c'est une invitation à chercher en soi l'espace qui nous relie aux autres. »"}
              </div>
              <div className="mt-1 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">— Kossi Homawoo · 179. Togo</div>
            </figcaption>
          </motion.figure>

          <motion.figure
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.1 }}
            className="md:col-span-5 group"
            data-testid="envisager-masks"
          >
            <div className="border border-fg/15 overflow-hidden bg-fg aspect-[3/4]">
              <img
                src="https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/gwvyxt05_MASKS.png"
                alt="Trois visages du monde — proposition d'installation intérieure, dialogue silencieux entre les continents"
                className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105"
                loading="lazy"
              />
            </div>
            <figcaption className="mt-4">
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre">{en ? "Interior · Dialogue of continents" : "Intérieur · Dialogue des continents"}</div>
              <div className="mt-1 font-serif italic text-lg md:text-xl leading-tight">
                {en ? "Three faces from elsewhere, held by the same light." : "Trois visages venus d'ailleurs, tenus par la même lumière."}
              </div>
              <div className="mt-1 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">{en ? "Provisional vision · Simulation" : "Vision provisoire · Simulation"}</div>
            </figcaption>
          </motion.figure>
        </div>

        <p className="mt-10 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50 max-w-2xl">
          {en ? "Research images — the final artistic project will be co-written with the invited artists." : "Images de recherche — le projet artistique final sera co-écrit avec les artistes invités."}
        </p>
      </section>

      {/* Teaser vers Le Carnet — la Fée et les fées à venir vivent désormais dans le Carnet */}
      <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1400px] mx-auto" data-testid="carnet-teaser">
        <Link to="/carnet/la-fee-du-canal" className="grid md:grid-cols-12 gap-8 md:gap-14 items-center group border-t border-b border-fg/15 py-14 md:py-20">
          <motion.figure
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 1 }}
            className="md:col-span-5"
          >
            <div className="border border-fg/15 overflow-hidden bg-fg/5 aspect-[3/4]">
              <img
                src="https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/3vqrhvp2_La%20f%C3%A9e%20du%20canal2.jpeg"
                alt="La Fée du Canal — Denis Cat"
                className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.03]"
                loading="lazy"
              />
            </div>
          </motion.figure>
          <div className="md:col-span-7">
            <div className="meta-label text-cinabre">§ {en ? "In the Journal · Chronicle of the Thread" : "Dans Le Carnet · Chronique du Fil"}</div>
            <h2 className="mt-4 font-serif font-light text-4xl md:text-6xl leading-[0.98] tracking-tight">
              {en ? <>The <em className="italic">Canal Fairy</em>,<br/>and those to come.</> : <>La <em className="italic">Fée du Canal</em>,<br/>et celles à venir.</>}
            </h2>
            <p className="mt-6 font-serif italic text-xl md:text-2xl leading-[1.4] text-fg/80 max-w-xl">
              {en ? "A forgotten artwork on a tree. A story recovered. A geography of presences to come." : "Une œuvre oubliée sur un arbre. Une histoire retrouvée. Une géographie de présences à venir."}
            </p>
            <div className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.24em] uppercase text-cinabre group-hover:gap-3 transition-all">
              {en ? "Read the chronicle →" : "Lire la chronique →"}
            </div>
          </div>
        </Link>
      </section>

      {/* Litanie finale */}
      <section className="mt-24 md:mt-32 bg-fg text-bg py-24 md:py-32 px-6 md:px-12" data-testid="recit-litanie">
        <div className="max-w-[1200px] mx-auto">
          <div className="meta-label text-cinabre">§ {en ? "The deep story of the Thread" : "Le récit profond du Fil"}</div>
          <h2 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight mt-6">
            {en ? <>A <em className="italic">human line</em>,<br/>drawn by hand.</> : <>Une <em className="italic">ligne humaine</em>,<br/>tracée de main d'homme.</>}
          </h2>

          <div className="mt-16 space-y-6 md:space-y-8">
            {litanie.map(([place, meaning], i) => (
              <StanzaLine key={place} delay={i * 0.05}>
                <div className="grid md:grid-cols-12 items-baseline gap-4 border-b border-bg/15 pb-6">
                  <div className="md:col-span-4 font-serif text-3xl md:text-5xl leading-tight tracking-tight">{place}</div>
                  <div className="md:col-span-7 md:col-start-6 font-serif italic text-2xl md:text-3xl leading-tight text-bg/85">{meaning}</div>
                </div>
              </StanzaLine>
            ))}
          </div>

          <p className="mt-16 font-serif italic text-2xl md:text-3xl leading-tight text-bg/80 max-w-3xl">
            {en ? "Loupian must become the final image of Canal'Art 2026. Not the most spectacular. The most emotional." : "Loupian doit devenir l'image finale de Canal'Art 2026. Pas la plus spectaculaire. La plus émotionnelle."}
          </p>
        </div>
      </section>
    </main>
  );
}
